import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { parseFullName } from 'parse-full-name';
import axios from 'axios';

import {
  message,
  Form,
  AutoComplete,
  Tooltip,
  Select,
} from 'antd';
// Custom DatePicker (blur-commit fix) landed on staging; use it here too.
import DatePicker from '../DatePicker';
import { WarningCircle, CheckCircle, Info } from '@phosphor-icons/react';
import ModalWrapper from './ModalWrapper';
import { extractReplyTiffWarnings, showReplyTiffWarningModal } from '../chartActions/replyTiffWarning';
import Input from '../Input';
import Checkbox from '../Checkbox';
import Button from '../Button';
import Typography from '../Typography';
import COLORS from '../../util/constants/colors';
import { taskCreatedByStamp, buildTaskTemplateNotes } from '../../utils/taskTemplateNotes';
import { computeEmaStandaloneTaskFileName, computeRecordFileName } from '../chartActions/helpers';
import './createTaskModal.scss';

const { Option } = Select;

const FieldLabel = ({ children }) => (
  <Typography
    className="label"
    variant="text"
    color={COLORS.TEXT_SECONDARY_LITE}
    size="xsmall"
    bold
  >
    {children}
  </Typography>
);

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

class CreateTask extends Component {
  // A real provider must be chosen before a task can be finalized. Templates may
  // carry a blank provider (manual-only), which arrives here as undefined, [], or
  // [''] — none of those count. Kareo passes a single string, EMA an array.
  static hasProvider(assignTo) {
    let list = [];
    if (Array.isArray(assignTo)) {
      list = assignTo;
    } else if (assignTo) {
      list = [assignTo];
    }
    return list.some((value) => value != null && String(value).trim() !== '');
  }

  constructor(props) {
    super(props);
    this.state = {
      sendToken: props.modalType.data.record,
      patientFirstName: this.props.modalType.data.patient_first_name || '',
      patientMiddleName: this.props.modalType.data.patient_middle_name || '',
      patientLastName: this.props.modalType.data.patient_last_name || '',
      patientDOB: this.props.modalType.data.dob || '',
      patientMrn: '',
      assignTo: undefined,
      autoUploadEmr: '',
      searchedNames: null,
      allProviderNames: [],
      task: '',
      searchedTasks: '',
      searchedTemplates: null,
      emaTaskTemplates: null,
      taskDetails: '',
      taskDetailsSearchOptions: null,
      dueDate: '',
      notes: taskCreatedByStamp(),
      priority: 'Normal',
      associateWithPatient: true,
      associateDoc: true,
      fileToChart: false,
      receiverType: this.props.modalType.data.receiverType,
      facilityName: '',
      facilityNames: [],
      searchedFacilities: [],
      apiCredentials: this.props.modalType.data.apiCredentials,
      showNameMismatchWarning: false,
      initialParsedName: '',
    };

    // Calculate initial name mismatch - this is determined once and never changes
    const normalizeSpaces = (str) => str ? str.trim().replace(/\s+/g, ' ') : '';
    const initialConstructedName = [
      this.props.modalType.data.patient_first_name,
      this.props.modalType.data.patient_middle_name,
      this.props.modalType.data.patient_last_name,
    ].filter(Boolean).join(' ').trim();
    const normalizedConstructedName = normalizeSpaces(initialConstructedName);
    const normalizedOriginalName = normalizeSpaces(this.props.modalType.data.name);

    this.state.initialParsedName = initialConstructedName;
    this.state.showNameMismatchWarning = this.props.modalType.data.name
                                         && normalizedConstructedName
                                         && normalizedOriginalName
                                         && normalizedConstructedName !== normalizedOriginalName;

    this.priorityOptions = ['Normal', 'High', 'STAT'];
    this.taskOptions = [
      'Authorization', 'Communication', 'Fax', 'Notify', 'Obtain', 'Order', 'Pathology', 'Prescribe', 'Reassure',
      'Recall', 'Re-Evaluate', 'Refer', 'Return To Clinic', 'Review', 'Schedule', 'Sign', 'Test',
    ];

    this.taskDetailsOptions = [
      'Additional Testing', 'Benign', 'Claim Review', 'Discuss at Follow-up', 'Follow-up Appt', 'Labs', 'PCP',
      'Medication', 'Normal', 'No Further Treatment', 'Procedure', 'Referring Provider', 'Results', 'Sign/Cosign',
      'Surgery',
    ];

    this.state.searchedTasks = this.taskOptions;
    this.state.taskDetailsSearchOptions = this.taskDetailsOptions;
    this.formRef = React.createRef();
  }

  async componentDidMount() {
    const { getEmaTaskProviders, getKareoTaskProviders, getTaskTemplatesByQuery } = this.props;
    const { sendToken } = this.state;
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');

    // Call the appropriate provider getter based on EMR type
    if (autoUploadEmr === 'Kareo') {
      await getKareoTaskProviders(sendToken);
    } else {
      await getEmaTaskProviders(sendToken);
    }

    // Get provider names from the appropriate source
    const providers = autoUploadEmr === 'Kareo'
      ? this.props.allKareoTaskProviders
      : this.props.allEmaTaskProviders;

    const providerNames = (providers || []).map((p) => {
      return typeof p === 'string' ? p : p?.name;
    }).filter(Boolean);

    if (providerNames.length === 1) {
      let newAssignTo = providerNames[0];
      if (autoUploadEmr === 'EMA') {
        // Set assign to as array
        newAssignTo = [newAssignTo];
      }
      this.setState({ assignTo: newAssignTo });
    }
    this.setState({ searchedNames: providerNames, allProviderNames: providerNames });
    this.setState({ autoUploadEmr });
    const query = `?email=${localStorage.getItem('m8_uid')}`;
    await getTaskTemplatesByQuery(query);
    this.setState({
      emaTaskTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
      searchedTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
    });

    const { patientFirstName, patientLastName } = this.state;
    if (autoUploadEmr === 'EMA' && this.props.modalType.data.name) {
      const patientName = this.props.modalType.data.name;
      if (!(patientFirstName || patientLastName)) {
        // Handle edge case where the patient_name has 2 surnames (this is common for Spanish names)
        if (patientName.includes(',') && patientName.split(',')[0].trim().includes(' ') && patientName.split(',')[0].trim().split(' ').length > 1) {
          const parsedNames = this.parseName(patientName);
          this.setState({
            patientFirstName: parsedNames.firstName,
            patientLastName: parsedNames.lastName,
          });
        } else {
          this.setState({
            patientFirstName: parseFullName(patientName).first,
            patientMiddleName: parseFullName(patientName).middle,
            patientLastName: parseFullName(patientName).last,
          });
        }
      }
    }

    if (this.state.autoUploadEmr === 'EMA' && this.state.apiCredentials) {
      try {
        const response = await axios.get('/api/v1/emr_facilities');
        const res = await response.data;

        this.setState({
          facilityNames: res.facilities,
          searchedFacilities: res.facilities,
        });
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    const { hideModal, triggerTaskCreation, triggerAutoUploadAndTaskCreation } = this.props;
    const {
      sendToken,
      patientFirstName,
      patientLastName,
      patientMiddleName,
      patientDOB,
      patientMrn,
      assignTo,
      task,
      taskDetails,
      dueDate,
      priority,
      notes,
      receiverType,
      associateWithPatient,
      associateDoc,
      fileToChart,
      facilityName,
    } = this.state;

    const isKareo = this.state.autoUploadEmr === 'Kareo';

    let formattedDOB = '';
    let formattedDueDate = '';
    if (patientDOB !== '') {
      const splitDate = patientDOB.split('/');
      formattedDOB = (associateWithPatient || isKareo) ? `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}` : '';
    }
    if (dueDate !== '') {
      const splitDate = dueDate.split('/');
      formattedDueDate = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }

    const fileDocToChart = fileToChart && associateWithPatient && !isKareo;

    const uploadFileName = computeRecordFileName(this.props.modalType.data);
    const standaloneFileName = computeEmaStandaloneTaskFileName({
      emr: this.state.autoUploadEmr,
      associateDoc,
      patientFirstName,
      patientMrn,
      fileToChart: fileDocToChart,
      record: this.props.modalType.data,
    });

    // For Kareo, always associate with patient and document
    // For other EMRs, respect the checkbox values
    // Every branch's result is captured: the ENG-3012 reply merge runs for any
    // Reply parent BEFORE the endpoint branches on upload_type, so even a plain
    // task_creation on a reply can come back carrying the TIFF-exclusion warning.
    let taskResult;
    if (fileDocToChart) {
      // Files the document in the patient's chart and creates the task (upload_and_task)
      taskResult = await triggerAutoUploadAndTaskCreation({
        sendToken, receiverType, fileName: uploadFileName || '', patientName: '', patientFirstName, patientLastName, patientMiddleName, patientDOB: formattedDOB, patientMrn, assignTo, task, taskDetails, priority, notes, dueDate: formattedDueDate, facilityName,
      });
    } else if (associateWithPatient || isKareo) {
      taskResult = await triggerTaskCreation({
        sendToken, receiverType, ...(standaloneFileName ? { fileName: standaloneFileName } : {}), patientName: '', patientFirstName, patientLastName, patientMiddleName, patientDOB: formattedDOB, patientMrn, assignTo, task, taskDetails, priority, notes, associateDoc: isKareo ? true : associateDoc, dueDate: formattedDueDate, facilityName,
      });
    } else {
      taskResult = await triggerTaskCreation({
        sendToken, receiverType, patientName: '', patientFirstName: '', patientLastName: '', patientMiddleName: '', patientDOB: '', patientMrn: '', assignTo, task, taskDetails, priority, notes, associateDoc, dueDate: formattedDueDate, facilityName,
      });
    }
    const { error, errorMessage } = this.props;
    hideModal();
    if (!error) {
      const tiffWarnings = extractReplyTiffWarnings(taskResult);
      if (tiffWarnings.length > 0) {
        showReplyTiffWarningModal({
          warnings: tiffWarnings,
          workflow: fileDocToChart ? 'legacy_task_upload_and_task' : 'legacy_task_creation',
        });
      } else {
        message.success({
          content: fileDocToChart
            ? 'Task will be created and the document filed to the chart shortly. We will notify you of any issues via email.'
            : 'Task will be created shortly. We will notify you of any issues via email.',
        });
      }
    } else {
      message.error({
        content: errorMessage,
      });
    }
  };

  parseName(name) {
    let firstName = '';
    let lastName = '';
    const parts = name.split(',');
    lastName = parts[0].trim();
    firstName = parts[1].trim();

    // Handle edge cases for Spanish names with multiple surnames
    if (lastName.split(' ').length > 1 && !lastName.includes('-')) {
      const lastNames = lastName.split(' ');
      lastName = `${lastNames[0]} ${lastNames[1]}`;
    }

    return { firstName, lastName };
  }

  render() {
    const { isLoading, hideModal, allEmaTaskProviders, allKareoTaskProviders } = this.props;
    const isKareo = this.state.autoUploadEmr === 'Kareo';
    const allTaskProviders = isKareo ? allKareoTaskProviders : allEmaTaskProviders;
    const modalTitle = isKareo ? 'Create Note in Your EMR' : 'Create Task in Your EMR';
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const {
      patientDOB, patientMrn, notes, associateWithPatient, patientFirstName, patientLastName, patientMiddleName, dueDate,
    } = this.state;

    const disableSubmitButton = () => {
      // Base requirements for all EMRs. A provider is always required to finalize —
      // templates can have a blank provider, but the user must pick a real one here.
      const missingAssignee = !CreateTask.hasProvider(this.state.assignTo);

      // For Kareo, patient info is always required (always associated with patient)
      // For other EMRs, only required if associateWithPatient is checked
      const missingPatientInfo = isKareo
        ? ((patientLastName === '' || patientFirstName === '' || patientDOB === ''))
        : (associateWithPatient && ((patientLastName === '' || patientFirstName === '' || patientDOB === '') && patientMrn === ''));

      // For Kareo, only assignee and patient info required
      if (isKareo) {
        return missingAssignee || missingPatientInfo;
      }

      // For other EMRs, task and taskDetails are also required
      return (
        missingPatientInfo
        || missingAssignee
        || this.state.task === ''
        || this.state.taskDetails === ''
      );
    };

    const handleAssociateWithPatient = (e) => {
      const linked = e.target.checked;
      // Filing to the chart requires a linked patient, so unlinking clears that choice.
      this.setState({ associateWithPatient: linked, ...(linked ? {} : { fileToChart: false }) });
    };

    const handleMiddleNameChange = (e) => {
      this.setState({ patientMiddleName: e.target.value });
    };

    const handleLastNameChange = (e) => {
      this.setState({ patientLastName: e.target.value });
    };

    const handleFirstNameChange = (e) => {
      this.setState({ patientFirstName: e.target.value });
    };

    const handleMrnChange = (e) => {
      this.setState({ patientMrn: e.target.value });
    };

    const handleDateChange = (_, dateString) => {
      this.setState({ patientDOB: dateString });
    };

    const handleDueDateChange = (_, dateString) => {
      this.setState({ dueDate: dateString });
    };

    const handleAssignToChange = (e) => {
      this.setState({ assignTo: e });
    };

    const handleAssignToSearch = (e) => {
      const allNames = this.state.allProviderNames || [];

      if (e) {
        const filtered = allNames.filter((n) => n.toLowerCase().includes(e.toLowerCase()));
        this.setState({ searchedNames: filtered });
      } else {
        this.setState({ searchedNames: allNames });
      }
    };

    const handleFacilitySearch = (value) => {
      if (value) {
        const filtered = this.state.facilityNames.filter(facility =>
          facility.name.toLowerCase().includes(value.toLowerCase())
        );
        this.setState({ searchedFacilities: filtered });
      } else {
        this.setState({ searchedFacilities: this.state.facilityNames });
      }
    };

    const handleFacilityChange = (value) => {
      if (!value) {
        this.setState({ facilityName: '', facilityId: null });
        return;
      }
      const selectedFacility = this.state.searchedFacilities.find(facility => facility.name === value);
      this.setState({ facilityName: selectedFacility.name, facilityId: selectedFacility.id });
    };

    const handleTaskSearch = (e) => {
      if (e) {
        const filtered = this.taskOptions.filter(task => task.toLowerCase().includes(e.toLowerCase()));
        this.setState({ searchedTasks: filtered });
      } else {
        this.setState({ searchedTasks: this.taskOptions });
      }
    };

    const handleTaskChange = (e) => {
      this.setState({ task: e });
    };

    const handleSearchTaskDetails = (e) => {
      this.setState({ taskDetails: e });
      const filteredOptions = this.taskDetailsOptions.filter(option => (
        option.toLowerCase().includes(e.toLowerCase())));
      this.setState({ taskDetailsSearchOptions: filteredOptions });
    };

    const handleTaskDetailsSelection = (e) => {
      this.setState({ taskDetails: e });
    };

    const handleNotesChange = (e) => {
      this.setState({ notes: e.target.value });
    };

    const handleAssociateDoct = (e) => {
      this.setState({ associateDoc: e.target.checked });
    };

    const handleFileToChart = (e) => {
      this.setState({ fileToChart: e.target.checked });
    };

    const checkFileToChart = () => {
      this.setState({ fileToChart: true });
    };

    const handlePriorityChange = (e) => {
      this.setState({ priority: e });
    };

    const handleTaskTemplateSearch = (e) => {
      const unfilteredObjects = this.state.emaTaskTemplates;
      if (e) {
        const filtered = this.state.emaTaskTemplates.map(template => template.task_name).filter(option => (option.toLowerCase().includes(e.toLowerCase())));
        const filteredObject = [];
        filtered.forEach((template) => {
          const index = this.state.emaTaskTemplates.map(t => t.task_name).indexOf(template);
          filteredObject.push(this.state.emaTaskTemplates[index]);
        });
        this.setState({ searchedTemplates: filteredObject });
      } else {
        this.setState({ searchedTemplates: unfilteredObjects });
      }
    };

    const handleTaskTemplateChange = (e) => {
      // if user clears out selection, we will reload the list of templates to choose from.
      if (!e) {
        handleTaskTemplateSearch();
        this.setState({
          notes: taskCreatedByStamp()
        });
        return;
      }
      const index = this.state.emaTaskTemplates.map(template => template.id).indexOf(e);
      const selectedTemplate = this.state.emaTaskTemplates[index];
      // A template can have a blank provider (manual-only) — default to an empty list
      // so the user is forced to pick a provider before submitting.
      const assigtoArray = selectedTemplate.assign_to_as_array || [];
      if (assigtoArray.includes('Ordering Provider')) {
        assigtoArray.splice(assigtoArray.indexOf('Ordering Provider'), 1);
      }
      handlePriorityChange(selectedTemplate.priority);
      handleTaskDetailsSelection(selectedTemplate.task_details);
      handleTaskChange(selectedTemplate.task_type);
      handleAssignToChange(assigtoArray);

      // Force both the state update and re-render
      const newNotes = buildTaskTemplateNotes(selectedTemplate.notes);
      this.setState({ notes: newNotes }, () => {
        // Get form instance from ref
        if (this.formRef.current) {
          this.formRef.current.setFieldsValue({
            notes: newNotes
          });
        }
      });
    };

    const noProviderMessage = 'Contact your Admin to add EMR Providers';

    // Charting clarity: creating a task does not file the document in the chart, so
    // spell out where the document ends up and offer the combined upload-and-task path.
    // chartFiled comes from a successful filing-type EmrUpload (not the legacy
    // is_uploaded_to_emr column, which only tracks browser-extension views).
    const alreadyUploaded = !!this.props.modalType.data.chartFiled;
    const showChartFilingOptions = this.state.autoUploadEmr === 'EMA' && associateWithPatient;
    let chartFilingCallout = null;
    if (showChartFilingOptions) {
      if (this.state.fileToChart) {
        chartFilingCallout = (
          <div className="create-task-modal__callout create-task-modal__callout--success" role="status" aria-live="polite">
            <CheckCircle size={18} weight="fill" />
            <div className="create-task-modal__callout-content">
              <Typography variant="text" size="small">
                The document will be filed in the patient's chart and attached to this task.
              </Typography>
            </div>
          </div>
        );
      } else if (alreadyUploaded) {
        chartFilingCallout = (
          <div className="create-task-modal__callout create-task-modal__callout--success" role="status" aria-live="polite">
            <CheckCircle size={18} weight="fill" />
            <div className="create-task-modal__callout-content">
              <Typography variant="text" size="small">
                This document is already filed in the patient's chart.
              </Typography>
            </div>
          </div>
        );
      } else {
        chartFilingCallout = (
          <div className="create-task-modal__callout create-task-modal__callout--warning" role="status" aria-live="polite">
            <WarningCircle size={18} weight="fill" />
            <div className="create-task-modal__callout-content">
              <Typography variant="text" size="small" bold>
                This creates a task only — it does not file the document in the patient's chart
              </Typography>
              <Typography variant="text" size="xsmall">
                {this.state.associateDoc
                  ? 'The document will be attached to the task, but it will not appear in the patient\'s chart documents.'
                  : 'The document will not be attached to the task or filed in the patient\'s chart.'}
              </Typography>
              <div className="create-task-modal__callout-actions">
                <Button
                  type="secondary"
                  size="xsmall"
                  label="File it to the chart"
                  onClick={checkFileToChart}
                />
              </div>
            </div>
          </div>
        );
      }
    }

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={{}}
        action={this.handleSubmit}
        actionName="Submit"
        width="700px"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={disableSubmitButton()}
        modalTitle={modalTitle}
        form="createTask"
        isDraggable
        wrapClassName="create-task-modal"
      >
        <Form
          ref={this.formRef}
          id="createTask"
          className="create-task-modal__form"
          onFinish={hideModal}
          layout="vertical"
          initialValues={{ notes }}
        >
          {this.state.showNameMismatchWarning && (
            <div className="create-task-modal__callout create-task-modal__callout--warning" role="status" aria-live="polite">
              <WarningCircle size={18} weight="fill" />
              <div className="create-task-modal__callout-content">
                <Typography variant="text" size="small" bold>
                  Patient Name Mismatch Detected
                </Typography>
                <Typography variant="text" size="xsmall">
                  Please verify the patient's name before sending to chart. Due to a recent
                  system migration, some previously edited values may be inconsistent.
                </Typography>
                <Typography variant="text" size="xsmall">
                  Extracted name:
                  {' '}
                  <span style={{ color: COLORS.PURPLE_PRIMARY_LITE }}>{this.state.initialParsedName}</span>
                </Typography>
                <Typography variant="text" size="xsmall">
                  Updated name:
                  {' '}
                  <span style={{ color: COLORS.PURPLE_PRIMARY_LITE }}>{this.props.modalType.data.name}</span>
                </Typography>
              </div>
            </div>
          )}

          {!isKareo && (
            <Checkbox
              checked={associateWithPatient}
              onChange={handleAssociateWithPatient}
            >
              Link task to patient chart
            </Checkbox>
          )}

          {(associateWithPatient || isKareo) && (
            <div className="create-task-modal__row">
              <Input
                label="Patient First Name"
                required
                name="patientFirstName"
                placeholder="First name"
                value={patientFirstName}
                onChange={handleFirstNameChange}
              />
              <Input
                label="Patient Middle Name"
                name="patientMiddleName"
                placeholder="Middle name"
                value={patientMiddleName}
                onChange={handleMiddleNameChange}
              />
              <Input
                label="Patient Last Name"
                required
                name="patientLastName"
                placeholder="Last name"
                value={patientLastName}
                onChange={handleLastNameChange}
              />
              <div className="create-task-modal__field">
                <FieldLabel>Patient Date of Birth*</FieldLabel>
                <DatePicker
                  onChange={handleDateChange}
                  name="patientDOB"
                  format={dateFormat}
                  value={patientDOB && moment(patientDOB, dateFormat)}
                />
              </div>
            </div>
          )}

          {((associateWithPatient && !isKareo) || (this.state.autoUploadEmr === 'EMA' && this.state.apiCredentials)) && (
            <div className="create-task-modal__row">
              {associateWithPatient && !isKareo && (
                <Input
                  label="Patient MRN (Optional)"
                  name="patientMRN"
                  placeholder="Patient MRN"
                  value={patientMrn}
                  onChange={handleMrnChange}
                />
              )}
              {this.state.autoUploadEmr === 'EMA' && this.state.apiCredentials && (
                <div className="create-task-modal__field">
                  <FieldLabel>Facility (Optional)</FieldLabel>
                  <Select
                    showSearch
                    placeholder="Facility"
                    value={this.state.facilityName || undefined}
                    optionFilterProp="children"
                    onChange={handleFacilityChange}
                    onSearch={handleFacilitySearch}
                    filterOption={false}
                    allowClear
                  >
                    {this.state.searchedFacilities.map((facility) => (
                      <Option key={facility.id} value={facility.name}>{facility.name}</Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          )}

          {this.state.autoUploadEmr === 'EMA' && (
            <div className="create-task-modal__field">
              <FieldLabel>Task Templates</FieldLabel>
              <Select
                showSearch
                placeholder="Select a template"
                optionFilterProp="children"
                onChange={handleTaskTemplateChange}
                onSearch={handleTaskTemplateSearch}
                filterOption={false}
                allowClear
              >
                {(this.state.searchedTemplates || []).map((template) => (
                  <Option key={template.id} value={template.id}>{template.task_name}</Option>
                ))}
              </Select>
            </div>
          )}

          {!isKareo && (
            <div className="create-task-modal__row">
              <div className="create-task-modal__field" style={{ flex: 2 }}>
                <FieldLabel>Task*</FieldLabel>
                <Select
                  showSearch
                  placeholder="Task"
                  value={this.state.task || undefined}
                  optionFilterProp="children"
                  onChange={handleTaskChange}
                  onSearch={handleTaskSearch}
                  filterOption={false}
                >
                  {this.state.searchedTasks.map((e) => (
                    <Option key={e} value={e}>{e}</Option>
                  ))}
                </Select>
              </div>
              <div className="create-task-modal__field" style={{ flex: 2 }}>
                <FieldLabel>Task Details*</FieldLabel>
                <AutoComplete
                  placeholder="Task Details"
                  options={this.state.taskDetailsSearchOptions.map((option) => ({ value: option }))}
                  value={this.state.taskDetails}
                  onSearch={handleSearchTaskDetails}
                  onSelect={handleTaskDetailsSelection}
                />
              </div>
              <div className="create-task-modal__field">
                <FieldLabel>Due Date</FieldLabel>
                <DatePicker
                  onChange={handleDueDateChange}
                  name="dueDate"
                  format={dateFormat}
                  value={dueDate && moment(dueDate, dateFormat)}
                />
              </div>
            </div>
          )}

          <div className="create-task-modal__row">
            <div className="create-task-modal__field">
              <FieldLabel>
                Assign to EMR Staff or Staff Group*
                {allTaskProviders && allTaskProviders.length === 0 && (
                  <Tooltip title="Please contact your Admin to add Providers">
                    <Info size={14} style={{ marginLeft: '4px', verticalAlign: 'text-bottom' }} />
                  </Tooltip>
                )}
              </FieldLabel>
              <Select
                mode={isKareo ? undefined : 'multiple'}
                showSearch
                placeholder="Select a provider"
                value={this.state.assignTo}
                optionFilterProp="children"
                onChange={handleAssignToChange}
                onSearch={handleAssignToSearch}
                filterOption={false}
                allowClear
                notFoundContent={noProviderMessage}
              >
                {(this.state.searchedNames || []).map((e) => (
                  <Option key={e} value={e}>{e}</Option>
                ))}
              </Select>
            </div>
            {!isKareo && (
              <div className="create-task-modal__field">
                <FieldLabel>Priority</FieldLabel>
                <Select
                  allowClear={false}
                  onChange={handlePriorityChange}
                  value={this.state.priority}
                >
                  {this.priorityOptions.map((option) => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <Form.Item name="notes">
            <Input
              label="Notes (optional)"
              textArea
              onChange={handleNotesChange}
              placeholder="Notes (optional)"
              name="notes"
              value={notes}
            />
          </Form.Item>

          {!isKareo && (
            <div className="create-task-modal__checkbox-group">
              <Checkbox
                checked={this.state.associateDoc}
                onChange={handleAssociateDoct}
                name="attachDocToTask"
              >
                Attach document to this task
              </Checkbox>
              {showChartFilingOptions && (
                <Checkbox
                  checked={this.state.fileToChart}
                  onChange={handleFileToChart}
                  name="fileToChart"
                >
                  Also file document in the patient's chart
                </Checkbox>
              )}
            </div>
          )}
          {chartFilingCallout}
        </Form>
      </ModalWrapper>
    );
  }
}

CreateTask.defaultProps = {
  error: false,
  errorMessage: null,
  allEmaTaskTemplates: {},
};

CreateTask.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  triggerTaskCreation: PropTypes.func.isRequired,
  triggerAutoUploadAndTaskCreation: PropTypes.func.isRequired,
  error: PropTypes.bool,
  allEmaTaskProviders: PropTypes.array.isRequired,
  allKareoTaskProviders: PropTypes.array.isRequired,
  getEmaTaskProviders: PropTypes.func.isRequired,
  getKareoTaskProviders: PropTypes.func.isRequired,
  getTaskTemplatesByQuery: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  allEmaTaskTemplates: PropTypes.object,
};

export default connect(state => ({
  allEmaTaskTemplates: state.userProfile.allEmaTaskTemplates,
  isLoading: state.records.isLoading,
  emailValidMessage: state.records.emailValidMessage,
  updatedLabel: state.records.updatedLabel,
  data: state.labels.data,
  selectedRecord: state.inboxTable.recordData,
  error: state.records.error,
  emaTaskProviders: state.auth,
  allEmaTaskProviders: state.userProfile.allEmaTaskProviders,
  allKareoTaskProviders: state.userProfile.allKareoTaskProviders,
  errorMessage: state.records.errorMessage,
  apiCredentials: state.records.apiCredentials,
}))(CreateTask);
